/**
 * Schema Version Manager
 *
 * All legacy migrations (v1-v6) have been completed and removed.
 * This module now only stamps new documents with the current schema version.
 */

import type { AppDocument } from '../schema'
import { isDebugMode } from './debug'

// ============================================================================
// Constants
// ============================================================================

/**
 * Current schema version.
 * Bump this when adding new structural changes to the document.
 * Legacy migrations 1-6 ran during earlier app versions and are no longer needed.
 */
export const CURRENT_SCHEMA_VERSION = 6

// ============================================================================
// Public API
// ============================================================================

/**
 * Ensure the document has the current schema version set.
 *
 * For new documents (no _schemaVersion), sets it to CURRENT_SCHEMA_VERSION.
 * For existing documents already at CURRENT_SCHEMA_VERSION, this is a no-op.
 *
 * @param doc - Current Automerge document (read-only outside a change)
 * @param apply - Applier over that document (`ApplyChange`, `document/internal/change-doc.ts`)
 */
export function ensureSchemaVersion(
  doc: AppDocument,
  apply: (fn: (d: AppDocument) => void) => void,
): void {
  if (doc._schemaVersion === CURRENT_SCHEMA_VERSION) {
    return
  }

  if (isDebugMode()) {
    console.log(
      `[Schema] Setting version from ${doc._schemaVersion ?? 'unset'} to ${CURRENT_SCHEMA_VERSION}`,
    )
  }

  apply((d) => {
    d._schemaVersion = CURRENT_SCHEMA_VERSION
  })
}
